import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  /** Value used for sorting + filtering (and default display if no render). */
  get: (row: T) => string | number;
  /** Custom cell content. */
  render?: (row: T) => ReactNode;
  numeric?: boolean;
  sortable?: boolean;   // default true
  filterable?: boolean; // default true
  /** If set, the cell becomes a clickable link that calls this. */
  onClick?: (row: T) => void;
  align?: 'left' | 'right';
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Trailing actions column (not sortable/filterable). */
  actions?: (row: T) => ReactNode;
  empty?: ReactNode;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Base filename (no extension) for the "Export CSV" button. Omit to hide it. */
  exportName?: string;
}

/** RFC-4180 cell escaping: quote when the value contains a comma, quote, or newline. */
function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function DataTable<T>({ columns, rows, rowKey, actions, empty, initialSort, exportName }: Props<T>) {
  const [sortKey, setSortKey] = useState<string | null>(initialSort?.key ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSort?.dir ?? 'asc');
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    if (!openFilter) return;
    const onClick = (e: MouseEvent) => { if (headRef.current && !headRef.current.contains(e.target as Node)) setOpenFilter(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenFilter(null); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [openFilter]);

  const sortable = (c: Column<T>) => c.sortable !== false;
  const filterable = (c: Column<T>) => c.filterable !== false;
  function uniqueVals(col: Column<T>): string[] {
    return Array.from(new Set(rows.map((r) => String(col.get(r))))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }
  function toggleSort(key: string) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  }
  function toggleVal(col: Column<T>, val: string) {
    setFilters((prev) => {
      const base = prev[col.key] ? new Set(prev[col.key]) : new Set(uniqueVals(col));
      if (base.has(val)) base.delete(val); else base.add(val);
      return { ...prev, [col.key]: base };
    });
  }
  function setAll(col: Column<T>, all: boolean) {
    setFilters((prev) => ({ ...prev, [col.key]: all ? new Set(uniqueVals(col)) : new Set() }));
  }
  function isFiltered(col: Column<T>): boolean {
    const f = filters[col.key];
    return !!f && f.size < uniqueVals(col).length;
  }

  let view = rows.filter((r) => columns.every((c) => {
    const f = filters[c.key];
    return !f || f.has(String(c.get(r)));
  }));
  if (sortKey) {
    const col = columns.find((c) => c.key === sortKey);
    if (col) {
      view = [...view].sort((a, b) => {
        const av = col.get(a), bv = col.get(b);
        const r = col.numeric ? Number(av) - Number(bv) : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? r : -r;
      });
    }
  }

  function exportCsv() {
    // Export the CURRENT view (respects active filters + sort), using each
    // column's scalar get() value — the same value shown/sorted/filtered on.
    const header = columns.map((c) => csvCell(c.label)).join(',');
    const body = view.map((r) => columns.map((c) => csvCell(c.get(r))).join(',')).join('\r\n');
    const csv = '﻿' + header + '\r\n' + body; // BOM so Excel reads UTF-8
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportName || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      {exportName && (
        <div className="table-export">
          <button className="btn ghost small" onClick={exportCsv} disabled={view.length === 0}
            title="Download the current view as a CSV file">⬇ Export CSV</button>
        </div>
      )}
      <table className="table data-table">
      <thead ref={headRef}>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={`th-tools${c.align === 'right' ? ' right' : ''}`}>
              {sortable(c)
                ? <span className="th-label" onClick={() => toggleSort(c.key)} title="Sort">{c.label}<span className="sort-ico">{sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span></span>
                : <span className="th-label-static">{c.label}</span>}
              {filterable(c) && (
                <button className={`filter-ico${isFiltered(c) ? ' on' : ''}`} title="Filter" onClick={(e) => { e.stopPropagation(); setOpenFilter(openFilter === c.key ? null : c.key); }}>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M1 2h14l-5.5 6.5V14l-3 1V8.5z" /></svg>
                </button>
              )}
              {openFilter === c.key && (
                <div className="filter-pop" onClick={(e) => e.stopPropagation()}>
                  <div className="filter-pop-actions">
                    <button className="btn-link" onClick={() => setAll(c, true)}>All</button>
                    <button className="btn-link" onClick={() => setAll(c, false)}>None</button>
                  </div>
                  <div className="filter-pop-list">
                    {uniqueVals(c).map((v) => {
                      const f = filters[c.key];
                      const checked = !f || f.has(v);
                      return (
                        <label key={v} className="filter-pop-item">
                          <input type="checkbox" checked={checked} onChange={() => toggleVal(c, v)} />
                          <span>{v || '—'}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </th>
          ))}
          {actions && <th></th>}
        </tr>
      </thead>
      <tbody>
        {view.length === 0 ? (
          <tr><td colSpan={columns.length + (actions ? 1 : 0)} className="empty" style={{ padding: 28 }}>{empty ?? 'No rows.'}</td></tr>
        ) : view.map((row) => (
          <tr key={rowKey(row)}>
            {columns.map((c) => (
              <td key={c.key} className={c.align === 'right' ? 'right' : undefined}>
                {c.onClick
                  ? <button className="cell-link" onClick={() => c.onClick!(row)}>{c.render ? c.render(row) : String(c.get(row))}</button>
                  : (c.render ? c.render(row) : String(c.get(row)))}
              </td>
            ))}
            {actions && <td className="right">{actions(row)}</td>}
          </tr>
        ))}
      </tbody>
      </table>
    </>
  );
}
